/** @jsxImportSource @emotion/react */
import React, { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTheme } from '../provider/useTheme';
import type { SemanticColorToken, SpacingToken, RadiusToken } from '../themes/types';
import type { IconName } from '../icons';
import { useTranslation } from '@vitvio/locales';
import type { FontToken } from '../themes';
import CoreIcon from './CoreIcon';

export type ChipVariantToken = 'solid' | 'flat';
export type ChipSizeToken = 'sm' | 'md' | 'lg';
export type ChipColorToken = 'default' | 'primary' | 'Ahead/AI' | 'warning' | 'danger';

export interface ChipProps {
  path?: string;
  values?: Record<string, string | number>;
  children?: React.ReactNode;
  iconLeft?: IconName;
  iconRight?: IconName;
  variant?: ChipVariantToken;
  size?: ChipSizeToken;
  rounded?: boolean;
  color?: ChipColorToken;
}

// Background color tokens for solid variant
const chipBgSolidTokens: Record<ChipColorToken, SemanticColorToken> = {
  default: 'tag.secondary.solid.bg.default',
  primary: 'tag.primary.solid.bg.default',
  'Ahead/AI': 'tag.ai.solid.bg.default',
  warning: 'tag.warning.solid.bg.default',
  danger: 'tag.critical.solid.bg.default',
};

// Background color tokens for flat variant
const chipBgFlatTokens: Record<ChipColorToken, SemanticColorToken> = {
  default: 'tag.secondary.bg.default',
  primary: 'tag.primary.bg.default',
  'Ahead/AI': 'tag.ai.bg.default',
  warning: 'tag.warning.bg.default',
  danger: 'tag.critical.bg.default',
};

// Foreground color tokens for solid variant
const chipFgSolidTokens: Record<ChipColorToken, SemanticColorToken> = {
  default: 'fg',
  primary: 'fg.inverted.constant',
  'Ahead/AI': 'fg.inverted.constant',
  warning: 'fg',
  danger: 'fg.inverted.constant',
};

// Foreground color tokens for flat variant
const chipFgFlatTokens: Record<ChipColorToken, SemanticColorToken> = {
  default: 'fg',
  primary: 'fg',
  'Ahead/AI': 'fg',
  warning: 'fg',
  danger: 'fg',
};

// Padding tokens for each size
const chipPaddingYTokens: Record<ChipSizeToken, SpacingToken> = {
  sm: '0.5',
  md: '0.5',
  lg: '0.5',
};

const chipPaddingXTokens: Record<ChipSizeToken, SpacingToken> = {
  sm: '2',
  md: '2',
  lg: '2',
};

const chipGapTokens: Record<ChipSizeToken, SpacingToken> = {
  sm: '1',
  md: '1',
  lg: '1',
};

const chipFontTokens: Record<ChipSizeToken, FontToken> = {
  sm: 'S_Medium',
  md: 'M_Regular',
  lg: 'L_Regular',
};

const chipRadiusTokens: Record<ChipSizeToken, RadiusToken> = {
  sm: 'md',
  md: 'lg',
  lg: 'lg',
};

export const CoreChip: React.FC<ChipProps> = memo(
  ({
    path,
    values,
    children,
    iconLeft,
    iconRight,
    variant = 'solid',
    size = 'sm',
    rounded = false,
    color = 'default',
  }) => {
    const { getColor, getRadius, getFont, getSpacing } = useTheme();
    const { t } = useTranslation();

    // All hooks must be called before any early returns
    const colors = useMemo(() => {
      const bgToken = variant === 'solid' ? chipBgSolidTokens[color] : chipBgFlatTokens[color];
      const fgToken = variant === 'solid' ? chipFgSolidTokens[color] : chipFgFlatTokens[color];
      return {
        background: getColor(bgToken),
        foreground: getColor(fgToken),
        foregroundToken: fgToken,
      };
    }, [getColor, variant, color]);

    const sizes = useMemo(() => {
      const radiusToken: RadiusToken = rounded ? 'full' : chipRadiusTokens[size];
      return {
        paddingY: getSpacing(chipPaddingYTokens[size]),
        paddingX: getSpacing(chipPaddingXTokens[size]),
        radius: getRadius(radiusToken),
        gap: getSpacing(chipGapTokens[size]),
      };
    }, [getSpacing, getRadius, size, rounded]);

    const fontProperties = useMemo(() => getFont(chipFontTokens[size]), [getFont, size]);

    const content = children ?? (path ? t(path, values) : null);
    if (!content) return null;

    return (
      <span
        css={css`
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${colors.foreground};
          font-family: ${fontProperties.fontFamily};
          font-size: ${fontProperties.size}px;
          font-weight: ${fontProperties.weight};
          line-height: ${fontProperties.lineHeight}px;
          letter-spacing: ${fontProperties.letterSpacing};
          background-color: ${colors.background};
          border-radius: ${sizes.radius};
          padding: ${sizes.paddingY} ${sizes.paddingX};
          gap: ${sizes.gap};
          white-space: nowrap;
        `}
      >
        <CoreIcon icon={iconLeft ?? null} size="md" color={colors.foregroundToken} />
        {content}
        <CoreIcon icon={iconRight ?? null} size="md" color={colors.foregroundToken} />
      </span>
    );
  },
);

CoreChip.displayName = 'CoreChip';

export default CoreChip;
